import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { ENVIRONMENT_CONFIG, EnvironmentConfig } from '@ipreo/northstar';
import { PlatformType } from '../types/muni-environment';
import { BenchmarkProvider, BenchmarkCurve } from '../types/benchmark.types';
import { MACV1Schema, MMDV1Schema } from './benchmark.service.types';

export interface CurveResult {
  data: BenchmarkCurve[];
  status: number;
}

interface GetConfig {
  url: string;
  params?: { [key: string]: string };
  headers?: { [key: string]: string };
  mapper: (data: any) => BenchmarkCurve[];
}

enum CurveRequestType {
  daily = 'daily',
  weekly = 'weekly'
}

@Injectable()
export class BenchmarkService {
  private readonly platform: PlatformType;
  private readonly apiHost: string;
  private readonly muniApiHost: string;

  constructor(
    private http: HttpClient,
    @Inject(ENVIRONMENT_CONFIG) config: EnvironmentConfig
  ) {
    this.platform = config.params.platform as PlatformType;
    this.apiHost = config.params.apiHost as string;
    this.muniApiHost = config.params.muniApiHost as string;
  }

  public getCurves(
    date: string,
    provider: BenchmarkProvider
  ): Observable<CurveResult> {
    return this.get(CurveRequestType.daily, provider, date);
  }

  public getWeeklyCurves(
    date: string,
    provider: BenchmarkProvider
  ): Observable<CurveResult> {
    return this.get(CurveRequestType.weekly, provider, date);
  }

  private get(
    curveType: CurveRequestType,
    provider: BenchmarkProvider,
    date: string
  ): Observable<CurveResult> {
    const { url, headers, params, mapper } = this.configForCall(
      curveType,
      provider,
      date
    );

    return this.http
      .get<{
        yieldCurves: any;
      }>(url, {
        observe: 'response',
        headers,
        params
      })
      .pipe(
        mergeMap((response) => {
          let data: BenchmarkCurve[] = [];
          if (response.status === 200) {
            data = mapper(response.body.yieldCurves);
          }

          return of({ data, status: response.status });
        })
      );
  }

  private configForCall(
    curveType: CurveRequestType,
    provider: BenchmarkProvider,
    date: string
  ): GetConfig {
    switch (provider) {
      case BenchmarkProvider.mac:
        // eslint-disable-next-line no-case-declarations
        const headers = {};
        if (this.platform === PlatformType.buyside) {
          headers['Authorization'] = 'BSP-JWT';
        }
        switch (curveType) {
          case CurveRequestType.weekly:
            return {
              url: `${this.muniApiHost}/ihsm/mbsc-weekly`,
              params: { date },
              headers,
              mapper: MACV1Schema.toModel
            };
          case CurveRequestType.daily:
            return {
              url: `${this.muniApiHost}/ihsm/mbsc`,
              params: { date },
              headers,
              mapper: MACV1Schema.toModel
            };
        }
        break;
      case BenchmarkProvider.mmd:
        switch (this.platform) {
          case PlatformType.buyside:
            switch (curveType) {
              case CurveRequestType.weekly:
                return {
                  url: `${this.apiHost}/v1/deals/fixed-income/municipal/MMDWeeklyYieldCurve`,
                  params: { date },
                  mapper: MMDV1Schema.toWeeklyModel
                };
              case CurveRequestType.daily:
                return {
                  url: `${this.apiHost}/v1/deals/fixed-income/municipal/MMDYieldCurve`,
                  params: { date },
                  mapper: MMDV1Schema.toModel
                };
            }
            break;
          case PlatformType.polaris:
            switch (curveType) {
              case CurveRequestType.weekly:
                return {
                  url: `${this.apiHost}/api/v1/MMDWeeklyYieldCurve/${date}`,
                  mapper: MMDV1Schema.toWeeklyModel
                };
              case CurveRequestType.daily:
                return {
                  url: `${this.apiHost}/api/v1/MMDYieldCurve/${date}`,
                  mapper: MMDV1Schema.toModel
                };
            }
        }
    }
    // Default fallback to avoid compile error
    return {
      url: '',
      mapper: () => []
    };
  }
}
